<template>
  <div class="w-full max-w-lg mx-auto">
    <div ref="timelineContainer" class="status-timeline relative min-h-[400px] pb-4">
      <!-- Status items with AnimatedList-style animation -->
      <Motion
        v-for="status in displayedStatuses"
        :key="status.id"
        as="div"
        :initial="{ scale: 0, opacity: 0 }"
        :animate="{
          scale: 1,
          opacity: 1,
          y: 0,
        }"
        :exit="{
          scale: 0,
          opacity: 0,
          y: 0,
        }"
        :transition="{
          type: 'spring',
          stiffness: 350,
          damping: 40,
        }"
        class="status-item pb-4 last:pb-0 flex items-center w-full"
      >
        <div 
          class="status-marker left-0 w-4 h-4 rounded-full border-2 border-white shadow-md mr-2 z-10"
          :style="`background-color: ${status.color}`"
        ></div>
        <div class="status-content bg-white lb-shadow p-4 rounded-lg shadow-sm w-full">
          <span class="text-sm text-gray-500">{{ status.date }}</span>
          <h3 class="text-lg font-semibold mt-1">{{ status.title }}</h3>
          <p v-if="status.description" class="text-gray-600 mt-1">{{ status.description }}</p>
        </div>
      </Motion>
    </div>
  </div>
</template>

<script setup>
import { Motion } from "motion-v";
import { ref, onMounted, onBeforeUnmount, computed } from "vue";

const props = defineProps({
  locale: {
    type: String,
    default: 'en'
  },
  delay: {
    type: Number,
    default: 2000
  }
});

const displayedStatuses = ref([]);
const nextIndex = ref(0);
const timelineContainer = ref(null);
const hasAnimated = ref(false);
let observer = null;

// Timeline statuses
const timelineData = computed(() => {
  const today = new Date();
  const foundDate = new Date(today);
  const inProgressDate = new Date(today);
  const removedDate = new Date(today);
  
  // Simulate realistic timeline
  foundDate.setDate(foundDate.getDate() - 1); // Yesterday
  inProgressDate.setDate(inProgressDate.getDate() - 1); // Yesterday
  // removedDate stays today
  
  const formatDate = (date) => {
    return props.locale === 'de' 
      ? date.toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit', year: 'numeric' })
      : date.toLocaleDateString('en-US', { month: '2-digit', day: '2-digit', year: 'numeric' });
  };

  return [
    {
      id: 'found',
      title: props.locale === 'de' ? 'Gefunden' : 'Found',
      description: props.locale === 'de' ? 'Leak durch automatischen Scan importiert.' : 'Leak imported from automated scan.',
      date: formatDate(foundDate),
      color: '#000000' // Black
    },
    {
      id: 'in_progress',
      title: props.locale === 'de' ? 'In Bearbeitung' : 'In Progress',
      description: props.locale === 'de' ? 'Takedown-Anfrage an Webseite gesendet.' : 'Takedown request sent to website.',
      date: formatDate(inProgressDate),
      color: 'rgb(245, 223, 77)' // Yellow
    },
    {
      id: 'removed',
      title: props.locale === 'de' ? 'Entfernt' : 'Removed',
      description: props.locale === 'de' ? 'Inhalt erfolgreich entfernt.' : 'Content successfully removed.',
      date: formatDate(removedDate),
      color: '#1f6feb' // Green
    }
  ];
});

onMounted(() => {
  // Set up intersection observer to trigger animation when component becomes visible
  if (timelineContainer.value) {
    observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting && !hasAnimated.value) {
            hasAnimated.value = true;
            setTimeout(() => {
              startTimelineAnimation();
            }, 500);
          }
        });
      },
      {
        threshold: 0.3, // Trigger when 30% of the component is visible
        rootMargin: '0px 0px -50px 0px' // Start animation slightly before fully visible
      }
    );
    
    observer.observe(timelineContainer.value);
  }
});

onBeforeUnmount(() => {
  if (observer) {
    observer.disconnect();
  }
});

async function startTimelineAnimation() {
  const statuses = timelineData.value;
  if (!statuses.length) return;

  // Add statuses one by one to the top, pushing others down
  for (let i = 0; i < statuses.length; i++) {
    displayedStatuses.value.unshift({
      ...statuses[i],
      id: `${statuses[i].id}-${Date.now()}`,
    });
    await wait(props.delay);
  }
}

async function wait(ms) {
  return new Promise((resolve) => setTimeout(resolve, ms));
}
</script>

<style scoped>
.status-timeline {
  position: relative;
}

.status-timeline::before {
  content: '';
  position: absolute;
  left: 8px;
  top: 0;
  bottom: 0;
  width: 2px;
  background-color: #e5e7eb;
  z-index: 1;
}

.status-item {
  position: relative;
  z-index: 2;
}

.lb-shadow {
  box-shadow: 0 8px 15px -3px rgba(0, 0, 0, 0.15), 0 4px 6px -2px rgba(0, 0, 0, 0.1);
}

/* Responsive adjustments */
@media (max-width: 640px) {
  .status-timeline {
    min-height: 350px;
  }
  
  .status-timeline::before {
    left: 7px;
  }

  .status-item {
    padding-bottom: 12px;
  }

  .status-item:last-child {
    padding-bottom: 0;
  }
  
  .status-content {
    font-size: 0.9rem;
  }
  
  .status-content h3 {
    font-size: 1rem;
  }
  
  .status-content p {
    font-size: 0.85rem;
  }
}
</style>